// Detect prompts in normal chat that would be better as a Work (agent) task.
//
// Work keeps going: research, tools, files, and finished drafts you can steer.
// Offer the optional suggestion only for substantial, multi-stage work.
// The broader catalog below also describes execution routing on the backend.
//
// Keep scenario patterns in sync with
// backend/inferencing/services/intent/work_mode_preference.js

const {
  isImageRequestQuery,
  isMusicCreationQuery,
  isVideoCreationQuery,
  is3DCreationQuery,
  isVectorRequestQuery,
  getMessageText,
} = require('./tool_intent_matcher');

const MIN_QUERY_LENGTH = 12;
const MIN_SCORE = 8;

const CHAT_IMAGE_INTENTS = new Set([
  'create-image', 'edit-image', 'create-vector', 'vectorize-image', 'upscale-image',
]);
const MEDIA_INTENTS = new Set([
  'create-image', 'edit-image', 'create-vector', 'vectorize-image',
  'upscale-image', 'create-video', 'image-to-video', 'create-music',
  'create-audio', 'create-3d', 'image-to-3d',
]);

const WORK_HEAVY_FILE_EXT = /\.(zip|tar|tgz|gz|rar|7z|js|jsx|ts|tsx|py|rb|go|rs|java|cpp|c|h|cs|php|swift|kt|sql|html|css|json|ya?ml|md|txt|pdf|docx?|xlsx?|pptx?|csv)$/i;
const ARCHIVE_FILE_EXT = /\.(zip|tar|tgz|gz|rar|7z)$/i;
const IMAGE_FILE_EXT = /\.(png|jpe?g|gif|webp|heic|heif|bmp|tiff?|svg)$/i;
// Backstop for media asks the shared media classifiers word differently
// ("draw a logo", "make a poster"). Kept in sync with the backend copy.
const MEDIA_REQUEST = /\b(generate|create|make|draw|design|render|compose)\b.{0,30}\b(image|picture|photo|logo|poster|banner|thumbnail|wallpaper|icon|illustration|avatar|song|music|video|clip|3d model)\b/i;
const WORK_HEAVY_MIME = /(zip|javascript|typescript|python|pdf|msword|spreadsheet|presentation|csv|json|xml|text\/plain)/i;

// Only request wording counts as evidence; URL slugs and query strings can
// contain arbitrary task keywords. Keep repository identity for clone asks.
function normalizeWorkModeText(message) {
  return (typeof message === 'string' ? message : '')
    .replace(/(?:https?:\/\/|www\.)[^\s<>"'`]+/gi, (url) => (
      /^(?:https?:\/\/|www\.)(?:www\.)?(github|gitlab)\.com\//i.test(url)
        ? 'https://github.com/repository' : '<link>'
    ))
    .replace(/[’‘]/g, "'").replace(/\s+/g, ' ').trim();
}

const CHAT_ONLY_REQUEST = [
  /\b(?:stay|keep (?:this|it|me)) in (?:normal )?chat(?: mode)?\b/i,
  /\b(?:don'?t|do not|never) (?:switch|redirect|move|send|upgrade)\b.{0,35}\bwork(?: mode)?\b/i,
  /^(?:please\s+)?(?:(?:can|could|would) you\s+)?(?:just |only )?(?:explain|describe|define|tell me about|help me understand|what (?:is|are|does)|(?:can|could) i|how (?:do|does|can|could|would|should) (?:i|we))\b/i,
  /^(?:please\s+)?(?:give|write|draft|suggest|improve|rewrite)\b.{0,30}\b(?:a |the |this |my )?prompt\b/i,
  /^(?:please\s+)?(?:summari[sz]e|translate|paraphrase|rephrase|explain)\b.{0,60}(?:\b(?:article|paragraph|sentence|phrase|passage|quote|text|video|clip|post|tweet|link)\b|<link>|["“])/i,
  /^(?:please\s+)?(?:don'?t|do not|no need to)\s+(?:download|fetch|save|transcribe|convert|build|create|generate|make|scrape|run)\b/i,
  /^(?:i|we)\s+(?:already|previously|just|yesterday)\s+(?:downloaded|saved|created|built|converted|transcribed|finished)\b/i,
];

function isExplicitChatRequest(message) {
  const text = normalizeWorkModeText(message);
  // An explanatory opener may be followed by a separate delegated task.
  // Negated commands and quoted examples are not that second instruction.
  const instructions = text.replace(/"[^"]*"|“[^”]*”|```[\s\S]*?```/g, '');
  const delegatesAfterExplanation = /(?:[.;!?]\s*(?:then\s+)?|\b(?:but|instead)\s+)(?:please\s+)?(?:download|fetch|transcribe|convert|build|create|generate|scrape|implement|run)\b/i.test(instructions)
    || (!HOW_TO_EXPLAIN.test(instructions)
      && /\band\s+(?:then\s+)?(?:save|export)\b.{0,35}\b(?:as|to|into)\s+(?:an?\s+)?(?:pdf|docx?|file|spreadsheet|csv|markdown)\b/i.test(instructions));
  return any(text, CHAT_ONLY_REQUEST.slice(0, 2))
    || (!delegatesAfterExplanation && any(text, CHAT_ONLY_REQUEST.slice(2)));
}

const TASK_VERBS = /\b(research|investigate|analyze|analyse|compare|extract|review|audit|reconcile|compile|draft|write|build|create|implement|refactor|migrate|scrape|browse|gather|outline|cite|recommend|summarize|merge|clean|convert|translate|process|plan|track|score|look up|find sources)\b/i;

function any(text, patterns) {
  if (!text) return false;
  for (let i = 0; i < patterns.length; i += 1) {
    if (patterns[i].test(text)) return true;
  }
  return false;
}

function countMatches(text, pattern) {
  if (!text || !pattern) return 0;
  const flags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`;
  const re = new RegExp(pattern.source, flags);
  const hits = text.match(re);
  return hits ? hits.length : 0;
}

// --- Vetoes (chat is the right place) --------------------------------------

const SIMPLE_QA = /^(what|who|when|where|why|how|is|are|can|do|does|did|should)\b/i;
const DEFINITIONAL_QA = /^(what('?s| is| are| was| were)|who (is|are|was|were)|when (is|was|did|were)|where (is|are)|why (is|do|does)|how (does|do) \w)/i;
const HOW_TO_EXPLAIN = /\b(explain how|how (do|does|can|would) (i|you|we)|what is the (best )?way to|walk me through how|tutorial for|teach me (how )?to)\b/i;
const TASK_IMPERATIVE = /\b(please (build|create|implement|refactor|go|do)|i (need|want) you to|go ahead and (build|create|implement)|do (this|it) for me)\b/i;
const SHORT_CREATIVE = /\b(haiku|limerick|joke|riddle|one[- ]liner|tweet)\b/i;
// One cover is a single image. A page-counted book still upgrades because
// MEDIA_PRODUCTION fires first and lifts this in readsLikeChat.
const SINGLE_COVER = /\b(book|album|magazine|comic|dvd|bluray|blu-ray)\s+covers?\b/i;
const SINGLE_SNIPPET = /\b(function to|a function that|snippet|one[- ]liner|this (line|function|regex|query)|reverse a string|fizzbuzz|hello world)\b/i;
const CAPABILITY_Q = /\b(can you make|do you (support|offer|have)|are you able to)\b/i;
// "can I import a csv into sheets?" — asking whether something is possible.
// First person singular only: "can you" and "can we" are often how people
// phrase a real request, but "can I" is asking about their own options.
const POSSIBILITY_Q = /^(can|could)\s+i\b/i;
// Export of the *previous chat reply* as a file — not converting an uploaded PDF.
const PDF_PREVIOUS = /\b(?:(?:make|export|save|turn)\b.{0,40}\b(?:this|that|it)\b.{0,16}\b(?:into|as|to)\s+(?:a\s+)?(?:pdf|docx?)\b|(?:make|export|save)\b.{0,40}\b(?:a\s+)?(?:pdf|docx?)\b.{0,40}\b(?:last|previous|this)\s+(?:answer|reply|message|chat)\b|(?:make|export|save)\b.{0,40}\b(?:your\s+|the\s+)?(?:last|previous)\s+(?:answer|reply|message).{0,24}\b(?:as|into|to)\s+(?:a\s+)?(?:pdf|docx?)\b|(?:pdf|docx?)\s+of\s+(?:your\s+|the\s+)?(?:last|previous)\s+(?:answer|reply|message)\b)/i;
const SHORT_TRANSLATE = /\btranslate this (sentence|phrase|word|line)\b/i;
const SHORT_SUMMARY = /\bsummarize this (paragraph|sentence|passage|quote)\b/i;
// Asking *about* a work product ("how much does a pitch deck cost?") instead
// of asking for one. These read as advice or trivia, so chat answers them.
const ADVICE_QA = /^(should (i|we)|is it (worth|better|ok|hard|easy)|do (i|we) (need|have to)|how (much|long|many|often)|did (they|he|she|it|we)|has (anyone|he|she|it)|which (is|are|one|of)|what'?s better)\b/i;
const LEARNING_INTENT = /\b(i (want|need) to learn|i'?d like to learn|help me learn|learn (about|more about)|teach myself)\b/i;

// --- Scenario catalog (Work is the right place) ---------------------------
// Mirror of backend/inferencing/services/intent/work_mode_preference.js.
// The two copies exist because the backend and the app deploy separately;
// backend/__tests__/work_mode_catalog_sync.test.js fails the build if they
// drift. Edit the backend copy, then re-run that test.

const PROJECT_SOFTWARE = [
  /\b(build|create|make|scaffold|implement|ship)\b.{0,50}\b(app|application|website|web app|landing page|saas|api|backend|frontend|codebase|repo|repository|service|platform|dashboard|full[- ]stack|chrome extension|firefox (addon|extension)|vscode extension|cli|command[- ]line|bot|plugin|integration)\b/i,
  /\b(refactor|rewrite|migrate|port|dockerize|containerize)\b.{0,40}\b(project|codebase|repo|repository|app|application|service|code)\b/i,
  /\b(this|the|my|our)\s+(codebase|repo|repository|project|app|application)\b.{0,80}\b(refactor|fix|update|migrate|implement|debug|patch|clean up|add tests|write tests|add auth|add authentication)\b/i,
  /\b(add|implement|wire up)\b.{0,30}\b(auth|authentication|oauth|ci|ci\/cd|logging|payments|billing|feature)\b.{0,40}\b(app|api|project|service|routes?|backend)\b/i,
  /\b(fix (all )?(the )?(failing )?tests|failing tests in this (project|repo|app)|test suite)\b/i,
  /\b(chrome|firefox|safari|vscode|vs code)\s+extension\b/i,
  /\bturn\b.{0,40}\binto\b.{0,20}\b(a |an )?(rest )?api\b/i,
  /\b(clone|fork)\b.{0,40}\b(github|gitlab|repo|repository)\b/i,
  /\bgithub\.com\/\S+.{0,80}\b(add|fix|implement|refactor|patch|dark mode)\b/i,
  // Session 6a999b23: "add a dark-mode toggle to the same converter.html"
  // stayed in Chat and dumped source. Filename overwrite is a Work file edit.
  /\b(add|apply|implement|wire up)\b.{0,48}\bdark[- ]?mode\b.{0,80}\b(\.html|\.css|website|webpage|page|app|converter|calculator|tool)\b/i,
  /\bkeep the (same )?filename\b/i,
  /\boverwrite (it|the file|the same file)\b.{0,40}\b(do not create|don'?t create|-1\.)/i,
  /\b(debug|fix)\b.{0,40}\b(the )?(app|application|project|codebase)\b.{0,40}\b(crash|bug|error|patch|failing)\b/i,
  // Ordinary words for the same work. People say "my website", not "my
  // codebase", and the noun lists above were written by engineers.
  // The tempered span keeps this off "create a banner image for my blog" —
  // an asset *for* a site is a media ask, not a change *to* the site.
  /\b(add|build|implement|set up|create|wire up)\b(?:(?!\b(image|picture|photo|logo|banner|poster|icon|illustration|graphic|thumbnail|avatar|mockup|video|song|music)s?\b).){0,44}\b(to|on|in|for)\s+(my|our|the|this)\s+(site|website|web ?app|app|page|landing page|blog|store|shop|dashboard|portal|server)\b/i,
  /\b(my|our|the|this)\s+(site|website|web ?app|app|page|blog|store|shop|dashboard|server|script)\b.{0,70}\b(is (slow|broken|down|buggy)|isn'?t working|not working|keeps? (crashing|breaking|failing)|figure out why|fix (it|that|this))\b/i,
  /\b(my|this|the)\s+(\w+\s+){0,2}(script|notebook|prototype|spreadsheet tool)\b.{0,40}\b(turn|convert|make)\b.{0,20}\b(it\s+)?into\b/i,
  /\b(set up|setup|add|automate)\b.{0,30}\b(ci|ci\/cd|github actions|docker|deploy(?:ment)? pipeline|landing page|stripe checkout)\b/i,
  /\b(write|build)\b.{0,20}\b(a |an )?(cli|bot|crawler|scraper|extension)\b/i,
  /\b(write|build|create|make)\b.{0,30}\b(an? )?(integration|plugin|webhook|connector)\b/i,
  /\b(write|generate|create)\b.{0,24}\b(documentation|api docs|readme)\b.{0,40}\b(api|codebase|project|library|service|repo|app)\b/i,
  /\b(write|add|generate)\b.{0,24}\b((unit|integration|e2e) )?tests\b.{0,40}\b(for (this|the|every|all|my|our)|in this (project|repo|app|module))\b/i,
  /\b(implement|build out)\b.{0,40}\b(the |this |attached )?(spec|prd|ticket|rfc)\b/i,
  /\b(review|fix|apply|address)\b.{0,24}\b(this|the)\s+(pr|pull request|diff|patch)\b/i,
  /\bmigrat(?:e|ing|ion)\b.{0,50}\b(from|onto|over to)\b.{0,40}\b(postgres|postgresql|mysql|mongo|mongodb|django|rails|typescript|python|aws|gcp)\b/i,
  // A sweep across a whole file or package rather than one function.
  /\b(add|apply|introduce|backfill)\b.{0,30}\b(type hints|types|typing|tests|docstrings|logging|error handling|comments)\b.{0,40}\b(this|the|every|all|my|our)\s+(whole\s+|entire\s+)?(module|file|codebase|project|repo|package|folder|directory|app)\b/i,
  // Promoting a throwaway into something installable.
  /\b(turn|convert|package|productioni[sz]e)\b.{0,44}\b(this|my|the)\s+(\w+\s+){0,2}(notebook|script|prototype|snippet|colab)\b.{0,40}\binto\b.{0,30}\b(a |an )?(package|library|module|cli|service|api|app|tool)\b/i,
  // Language ports are a whole-program rewrite, not a snippet translation.
  /\b(port|rewrite|convert|translate)\b.{0,44}\bfrom\s+(python|javascript|typescript|java|ruby|php|go|rust|c\+\+|c#|perl|scala|kotlin|swift|cobol|matlab)\b.{0,24}\bto\s+(python|javascript|typescript|java|ruby|php|go|rust|c\+\+|c#|perl|scala|kotlin|swift)\b/i,
];

const MULTI_STEP = [
  /\b(first|step 1|1\.)\b[\s\S]{8,}?\b(then|next|after that|step 2|2\.|and then|finally)\b/i,
  /\b(and then|then (create|build|write|implement|research|analyze|analyse|edit|update|generate|draft|make)|after that|once (you('ve| have)|that's) done)\b/i,
  /\bstep by step\b.{0,40}\b(build|implement|create|research|analyze|analyse|go through|plan|do)\b/i,
  /\bin (two|three|four|\d+)\s+(phases|steps|stages)\b/i,
];

const RESEARCH_DELIVERABLE = [
  // The trailing verb is what makes this a deliverable rather than a
  // lookup. Synthesis verbs count as much as authoring ones — "research X
  // and summarize the evidence" is the same job as "research X and write it
  // up", and only the second used to match.
  /\b(research|investigate|look up|look into|dig into|read up on)\b.{0,80}\b(and (then )?(write|draft|create|build|compile|produce|make|save|summari[sz]e|synthesi[sz]e|report|recommend|give me|tell me|show me|break (it )?down))\b/i,
  /\b(thorough|comprehensive|in-?depth|deep dive|full|sourced)\b.{0,40}\b(research|report|analysis|brief|literature review|memo)\b/i,
  /\b(competitive analysis|market research|due diligence|literature review|industry analysis)\b/i,
  /\b(write|create|draft|compile|save)\b.{0,40}\b(a |an )?(sourced |polished )?(brief|white paper|report|memo|briefing)\b/i,
  /\b(gather|find|collect)\b.{0,40}\b(everything|all (the )?(sources|papers|coverage)|the latest)\b.{0,50}\b(write|brief|report|summarize|compile)\b/i,
  /\b(latest (developments|papers|research|news))\b.{0,60}\b(summarize|brief|report|save|document|sources)\b/i,
  /\b(find sources|cite (everything|sources)|with sources)\b.{0,40}\b(brief|report|write|draft|recommend)\w*\b/i,
  /\bresearch the latest\b/i,
  // Gathering that ends in synthesis rather than a named document. The
  // object list is what keeps this off "find the answer and summarize it".
  /\b(find|gather|pull|dig up|look up|round up)\b.{0,70}\b(studies|papers|research|sources|articles|reviews|coverage|data|options|tools|vendors|products|listings|jobs|candidates|suppliers)\b.{0,70}\b(summari[sz]e|synthesi[sz]e|compare|recommend|write|compile|short ?list|tell me which)\w*\b/i,
  /\bresearch\b.{0,90}\b(tell me which|recommend|help me (choose|decide|pick)|which (one )?(to|should i)\b)/i,
  /\b(compiled? into|distill(?:ed)? into)\b.{0,30}\b(a |an )?(report|brief|memo|document)\b/i,
  // Web lookup that has to land in a file. The lookup alone is chat's job.
  /\b(search|scour|browse|check|scan)\b.{0,24}\b(the web|the internet|online|the news)\b.{0,90}\b(document|doc|report|brief|memo|deck|slides|presentation|spreadsheet|file|write[- ]?up|summary)\b/i,
  /\b(find|pull|gather)\b.{0,30}\b(the latest|recent|current)\b.{0,30}\b(news|coverage|headlines|prices|updates)\b.{0,90}\b(document|doc|report|brief|memo|deck|spreadsheet|file|write[- ]?up|summary)\b/i,
];

const FILE_DOCUMENT_WORK = [
  /\b(these|the (attached|uploaded)|attached|uploaded)\s+(files?|docs?|documents?|pdfs?|notes|spreadsheets?|sources?|spec|prd)\b/i,
  /\b(go through|look through|work through|read through)\b.{0,30}\b(these|the|all|this)\s+(files?|docs?|documents?|pdfs?|notes|items|invoices|pages|sources|list|material)\b/i,
  // "analyze this lease" is the same ask as "review this lease"; the verb
  // list was just short.
  /\b(review|redline|audit|analy[sz]e|go over|look over|read through|check)\b.{0,40}\b(contract|agreement|nda|terms|lease|policy|offer letter|settlement)\b/i,
  /\b(turn|convert|export|transform)\b.{0,40}\b(this|the|my)\s+(pdf|docx?|document|deck|notes|file)\b.{0,40}\b(into|to)\b.{0,30}\b(a\s+)?(slide\s*deck|slides?|deck|presentation|spreadsheet|workbook|report)\b/i,
  /\bextract\b.{0,40}\b(tables?|data|action items|clauses)\b.{0,40}\b(pdf|document|spreadsheet|sheet)\b/i,
  /\b(clean( up)?|cleanup)\b.{0,20}\b(this|the)\s+(csv|dataset|data|spreadsheet)\b/i,
  /\b(merge|combine)\b.{0,30}\b(these|the)\s+(documents?|files|pdfs|docs)\b/i,
  /\btranslate this entire (document|file|pdf|contract)\b/i,
  /\b(process|reconcile)\b.{0,30}\b(invoices?|receipts?|statements?|transactions?)\b/i,
  /\b(rewrite|polish|proofread)\b.{0,30}\b(attached|uploaded|this|the)\s+(notes|draft|document|essay|memo)\b/i,
  /\b(edit|update|revise|modify|fix)\b.{0,30}\b(this|the|these|attached|uploaded)\s+(file|files|doc|docs|document|documents|spreadsheet|code|script|pdf)\b/i,
];

const ARTIFACT_AGENT = [
  /\b(create|build|make)\b.{0,40}\b(spreadsheet|workbook|xlsx)\b.{0,60}\b(compar|criteri|score|recommend|option|decision)\w*\b/i,
  /\b(create|build|make)\b.{0,40}\b(presentation|slide deck|slides|pptx|deck)\b.{0,50}\b(from this|speaker notes|research|material|audience)\b/i,
  /\b(write|draft|create)\b.{0,30}\b(a |an )?(project plan|migration plan|90[- ]day plan|runbook|spec|specification|proposal|operating agreement|terms of service)\b/i,
  /\b(save|export)\b(?!.{0,48}\b(last|previous)\s+(answer|reply|message))\b.{0,20}\b(as|it as)\b.{0,15}\b(xlsx|pptx|docx|pdf|a (document|file|spreadsheet|deck))\b/i,
  /\b(content calendar|email sequence|drip campaign|outreach (list|sequence)|follow[- ]?up sequence|nurture sequence|cold email (sequence|campaign))\b/i,
  /\b(plan with owners|tracker spreadsheet|gantt)\b/i,
  /\bturn (this|the|my|these)\s+(material|notes|outline|bullets|bullet points)\b.{0,40}\binto\b.{0,30}\b(slides|deck|presentation|document|proposal|paper|brief)\b/i,
  // Asking for a file, not just an answer in the thread.
  /\b(make|create|build|put together|generate|give)\b.{0,24}\b(me )?(a |an )?(document|doc|word doc|docx|report|brief|memo|write[- ]?up|one[- ]pager)\b/i,
];

// Discovery that has to land somewhere. "Find X" alone is a search and chat
// handles it; "find X and put it in a spreadsheet" is an afternoon of
// collecting, deduping, and formatting.
const FIND_AND_COLLECT = [
  /\b(find|search for|look for|hunt for|gather|collect|pull|compile|source|catalog|catalogue|inventory)\b.{0,110}\b(in|into|as|to)\s+(a\s+)?(spreadsheet|sheet|table|doc|document|csv|file|list)\b/i,
  /\b(find|search for|look for|gather|collect|pull)\b.{0,90}\b(put|save|drop|organize|sort)\b.{0,40}\b(spreadsheet|sheet|table|doc|document|csv|file|list)\b/i,
  /\b(find|pull together|put together)\b.{0,24}\b(me\s+)?(a\s+)?(short ?list|round[- ]?up|comparison)\s+of\b/i,
  /\b(find|research|look up)\b.{0,60}\b(near me|in my area|local|nearby)\b.{0,70}\b(compare|reviews?|prices?|quotes?|short ?list|options|recommend)\w*\b/i,
];

// Work that repeats on a clock rather than finishing once.
const MONITOR_RECURRING = [
  /\b(monitor|track|watch|keep an eye on|keep tabs on|check|scan)\b.{0,70}\b(every (day|hour|week|month|morning)|each (day|week)|daily|hourly|weekly|monthly|continuously|on a schedule|regularly)\b/i,
  /\b(monitor|track|watch|keep an eye on|alert me|notify me|let me know|ping me|tell me)\b.{0,60}\b(when|if|as soon as)\b.{0,50}\b(changes?|drops?|rises?|goes? (up|down|live)|becomes? available|back in stock|in stock|posts?|publishes|updates?|expires?)\b/i,
  /\b(send|email|give|deliver) me\b.{0,40}\b(a\s+)?(daily|weekly|monthly|regular|recurring)\s+(digest|summary|report|round[- ]?up|update|briefing|newsletter)\b/i,
  /\b(monitors?|tracks?|watch(es)?)\b.{0,50}\b(mentions|prices?|competitors?|listings?|rankings?|reviews?|coverage|changes)\b/i,
];

// Moving a body of data between tools or formats. Always a batch — the user
// says "my notes", not "this note" — and always mechanical enough that chat
// can only describe the steps instead of doing them.
const TOOL_MIGRATION = [
  // Named tools only. Moving between two products is inherently a bulk job,
  // so the tool name carries the scope on its own. Bare formats ("convert
  // this to json") are deliberately not here — that is a one-liner.
  /\b(move|migrate|transfer|port|import|export|convert|sync|copy|turn|rebuild)\b.{0,60}\b(from|into|to|over to)\b.{0,40}\b(notion|obsidian|evernote|roam|logseq|airtable|trello|asana|jira|linear|monday|clickup|hubspot|salesforce|pipedrive|mailchimp|klaviyo|quickbooks|xero|freshbooks|wave|shopify|woocommerce|wordpress|squarespace|wix|webflow|ghost|substack|gmail|outlook|dropbox|google drive|sheets|static site|sqlite|postgres|mysql|mongo)\b/i,
  // Formats need the scope spelled out, which "all my notes" supplies.
  /\b(all (of )?(my|our)|every (one of )?(my|our)?|my whole|our whole|my entire|our entire)\b.{0,34}\b(pages?|notes?|posts?|articles?|records?|contacts?|rows?|entries|files?|docs?|documents?|tasks?|issues?|tickets?|customers?|subscribers?|invoices?|photos?|bookmarks?|library|archive)\b.{0,40}\b(into|to|over to|as|in)\b/i,
  /\b(bulk|mass|batch)\s+(import|export|convert|upload|migrate|update|rename|tag|resize)\b/i,
];

// Two artifacts, one question: what is different? The comparison itself is
// the deliverable, which is why DECISION_COMPARE (which wants a pick) misses
// these.
const DIFF_RECONCILE = [
  /\b(diff|compare|contrast|cross[- ]?reference)\b.{0,40}\b(these|the|both|two|2)\s+(\w+\s+){0,2}(documents?|contracts?|agreements?|files?|versions?|drafts?|reports?|spreadsheets?|exports?|lists?|policies|statements?|quotes?|bids?)\b/i,
  /\bwhat (changed|is different|are the differences|differs)\b.{0,60}\b(between|from|versus|vs\.?)\b/i,
  /\b(compare|reconcile|stack up)\b.{0,60}\b(last|previous|prior|this|next)\s+(quarter|month|year|week|version|release|draft|period)\b.{0,60}\b(to|with|against|versus|vs\.?)\b/i,
  /\b(side[- ]by[- ]side|line[- ]by[- ]line|point[- ]by[- ]point)\b.{0,44}\b(compar|review|check|analy|breakdown)\w*\b/i,
];

// One message, several finished versions of the same thing. Each variant is
// its own pass, so the count is what makes it work rather than a rewrite.
const MULTI_VARIANT = [
  /\b(in|for|across)\s+(two|three|four|five|six|\d+)\s+(different\s+)?(tones?|voices?|styles?|audiences?|segments?|personas?|languages?|formats?|lengths?|variations?|versions?|channels?|platforms?)\b/i,
  /\b(write|rewrite|adapt|tailor|reword|redo|rework|spin)\b.{0,44}\bfor\s+(an?\s+)?\w+,\s+(an?\s+)?\w+,?\s*and\s+(an?\s+)?\w+\b/i,
  /\b(adapt|tailor|customi[sz]e|rewrite|version)\b.{0,44}\b(for|to)\b.{0,40}\b(each|both|every|different|multiple|several)\s+(audience|segment|persona|market|channel|platform|region|locale|team)s?\b/i,
  /\b(one (version|variant|cut|take) (for|per)|a version for each|versions? for both)\b/i,
  /\b(this|the) same\b.{0,40}\b(adapted|tailored|rewritten|reworked|versioned|but) for\b/i,
];

// Work with a finish line the agent can test itself against. Chat can suggest
// one change; hitting a number takes attempts and measurement.
const ITERATE_TO_TARGET = [
  /\b(optimi[sz]e|improve|tune|speed up|reduce|cut|shrink|trim|get|bring)\b.{0,60}\b(until|to (under|below|less than|about)|down to|under|below|within)\b.{0,24}\b(\d|one|two|three|four|five|ten)\b/i,
  // The until-clause has to name a finish line. Without the target this
  // catches ordinary sequencing — "wait until it loads then click submit".
  /\buntil (it|they|this|that|the \w+)\s+(is|are|runs?|loads?|passes|works?|drops?|hits?|reaches|fits|compiles|builds)\b.{0,30}\b(under|below|less than|clean|green|correct|right|passing|stable|\d)/i,
  /\b(keep (going|trying|iterating|tweaking|adjusting|tuning|refining))\b.{0,44}\b(until|till)\b/i,
];

// Studying something that already exists to learn from it. The subject lives
// on the web, so this is a browse-and-synthesize run, not a recall question.
const TEARDOWN_ANALYSIS = [
  /\b(teardown|tear down|break ?down|deconstruct|dissect)\b.{0,44}\b(this|the|their|a|my)\s+(site|website|page|landing page|product|app|funnel|deck|campaign|pricing|strategy|copy|onboarding)\b/i,
  /\b(analy[sz]e|study|look at|examine|dig into|research|audit|review)\b.{0,50}\b(competitors?|competition|rivals?|their (site|product|pricing|strategy|copy|funnel|onboarding))\b/i,
  /\b(analy[sz]e|look at|study|audit|review|critique)\b.{0,40}\b(this|their|the|a|my)\s+(pricing|landing|checkout|signup|home)\s*page\b/i,
  /\breverse[- ]engineer\b/i,
  /\b(figure out|work out|find out)\b.{0,44}\b(how|what|why)\b.{0,44}\b(they|their|this site|this app|this product|competitors?)\b.{0,40}\b(do|does|did|are|is|rank|ranks|charge|charges|target|targets|work|works)\w*\b/i,
];

// "Why did this happen?" about something real, where the answer has to be
// found rather than recalled. Chat guesses from priors; the agent can pull
// the data, check the pages, and rule things out. DEFINITIONAL_QA still
// sends "why is the sky blue" to chat.
const DIAGNOSE_INVESTIGATE = [
  /\b(figure out|find out|work out|diagnose|investigate|dig into|get to the bottom of)\b.{0,50}\b(why|what (happened|caused|went wrong|is going on)|the (cause|reason))\b/i,
  /\bwhy\b.{0,70}\b(dropped|fell|declined|tanked|spiked|stopped working|isn'?t working|aren'?t working|not (converting|ranking|selling|loading)|went (up|down))\b.{0,70}\b(dig|find out|figure out|investigate|look into|analy[sz]e|troubleshoot)\w*\b/i,
  /\b(my|our)\b.{0,40}\b(conversion|open rate|click rate|churn|traffic|ranking|revenue|sales|signups|retention|engagement|bounce rate|ad spend|roas|margin)s?\b.{0,50}\b(dropped|fell|declined|tanked|spiked|went (up|down)|is (down|up))\b/i,
  /\broot[- ]cause\b/i,
];

// A script or an automation is a build: it has to run, and getting it to run
// takes attempts. "What is a cron job" stays in chat via DEFINITIONAL_QA.
const AUTOMATION_SCRIPTING = [
  /\b(write|build|create|make|set up)\b.{0,30}\b(a |me a |an )?(script|macro|automation|cron ?job|workflow|zap|shortcut|bot|agent|scraper|pipeline)\b.{0,60}\b(that|to|which|for)\b/i,
  /\bautomate\b.{0,50}\b(my|our|this|the|these)\b/i,
  /\b(stop doing|so i (stop|don'?t have to) doing?|instead of doing)\b.{0,40}\b(it|this|that|them)\b.{0,24}\b(by hand|manually|myself|every (week|day|month))\b/i,
];

// Handing the model a working role rather than a question. The continuation
// verb is required: without it this catches roleplay ("act as a pirate"),
// and teaching roles are deliberately absent so tutoring stays in chat.
const AGENT_FRAMING = [
  // The separator alternation cannot be wrapped in \b: a comma is not a
  // word character, so \b,\b never matches.
  /\b(act as|be|you are|you'?re)\s+(my|our)\s+(research assistant|analyst|project manager|producer|recruiter|consultant|strategist|editor|accountant|bookkeeper|agent|assistant|chief of staff|operator)\b.{0,80}(?:\band\b|,|:)\s*.{0,50}\b(dig|research|find|build|write|draft|analy[sz]e|go|organize|plan|review|get|tell|figure|put together|pull)\w*\b/i,
  /\b(pretend|imagine) (you are|you'?re) (a|an|my|our)\b.{0,40}\b(recruiter|analyst|consultant|researcher|auditor|editor|project manager|strategist)\b.{0,60}(?:\band\b|,)/i,
  /\b(i want you to|i need you to) (be|act as|work as)\b.{0,40}\b(my|our|the)\b/i,
];

// Turning a pile of physical or scattered things into a structured record.
const CATALOG_INVENTORY = [
  /\b(catalog|catalogue|inventory|digiti[sz]e|index)\b.{0,50}\b(everything|all|these|those|my|our|the)\b.{0,44}\b(photos?|pictures?|receipts?|books?|records?|items?|collection|library|files?|documents?|invoices?|cards?|parts?|assets?|equipment)\b/i,
  /\b(catalog|catalogue|inventory|digiti[sz]e)\b.{0,40}\b(and|then|into)\b/i,
];

// Getting ready for a specific high-stakes conversation. The work is the
// research and the rehearsal material, not the answer. "How do I negotiate
// salary" is a question and HOW_TO_EXPLAIN keeps it in chat.
const PREP_FOR_EVENT = [
  /\b(prep(are)? me for|prep for|get me ready for|help me (prep(are)?|get ready) for|prepare my case for)\b.{0,60}\b(interview|negotiation|meeting|pitch|presentation|exam|deposition|review|audit|promotion|raise|defense|hearing|deposition)\b/i,
  /\b(negotiat(e|ing))\b.{0,44}\b(my |our |a |the )?(salary|offer|raise|comp|contract|rate|price|lease|settlement|severance)\b/i,
  /\b(build|make|prepare|put together)\b.{0,30}\b(my |our |the )?case for\b/i,
];

const WEB_AGENT = [
  /\b(scrape|crawls?|spider)\b/i,
  /\b(go through|browse|visit)\b.{0,30}\b(this|the|these)\s+(website|site|page|pages|urls?|links)\b/i,
  /\b(extract|collect|pull)\b.{0,40}\b(from (the|this|these) (site|website|page|pages|urls?))\b/i,
  /\bbrowse\b.{0,40}\b(urls?|links|pages)\b.{0,40}\b(compar|summar|extract|produce|spreadsheet)\w*\b/i,
];

// Pulling media or content off the web and handing it back as a file, or
// reshaping a file the user already has. These tasks need download or
// transformation tools, not a page summary or a generated chat document.
// URL-only requests retain a source marker without trusting URL keywords.
const MEDIA_FETCH_CONVERT = [
  /\b(?:summari[sz]e|translate)\s+(?:this|the)\s+article\b.{0,40}\band\s+(?:then\s+)?(?:save|export)\b.{0,25}\bas\s+(?:an?\s+)?(?:pdf|docx?|file|markdown)\b/i,
  // "download this video", "grab the audio from this tweet". The determiner
  // is what separates a request from "can you download videos?", which is
  // a capability question.
  /\b(download|grab|rip|save|pull|fetch|extract)\b\s+(?:me\s+)?(?:a copy of\s+)?(?:this|that|the|these|those|all the|all of the|every|each)\s+(?:[\w'-]+\s+){0,3}(video|clip|audio|mp3|mp4|gif|movie|film|song|track|footage|reel|tiktok|short|stream|livestream|thumbnail|image|photo|picture|pic|file|pdf|attachment|subtitle|caption|transcript|playlist|episode|podcast|post|tweet|thread|article)s?\b/i,
  // Bulk retrieval: a playlist, a gallery, every reply in a thread.
  /\b(download|save|grab|rip|pull|fetch|get)\b.{0,30}\b(all|every|each|the whole|the entire|the full)\s+(?:[\w'-]+\s+){0,2}(playlist|channel|album|gallery|thread|feed|images?|photos?|pictures?|videos?|clips?|episodes?|pdfs?|files?|attachments?|pages?|replies|comments)\b/i,
  // The download is the stated purpose: "get this clip so I can download
  // it", "give me a download link". The verb in front can be anything.
  /\b(get|give|send|fetch|grab|download|save|make|want|need)\b.{0,85}\b(so (?:that )?i can (?:download|save) (?:it|this|that|them|these)|(?:download(?:able)?|direct|raw) (?:link|url|file)|(?:link|url) (?:to|so i can) download|to download (?:it|this|that|them|later|offline)|for offline (?:viewing|listening|use|playback)|(?:mp4|mp3|gif) file)\b/i,
  /^(?:(?:please|can you|could you|would you)\s+)*(?:download|fetch|save|grab)\s+(?:this|that|it|these)?\s*:?\s*<link>/i,
  // Speech to text. "what is a transcript" stays in chat via DEFINITIONAL_QA.
  /\b(transcribe|transcription (?:of|for)|(?:get|pull|grab|give) (?:me )?(?:the |a )?transcript (?:of|for|from)|(?:generate|create|make|add|burn in|write) (?:the |me )?(?:subtitles|captions|srt|closed captions|an srt))\b.{0,30}(?:\b(this|that|the|these|my|all|every|each)\b|<link>)/i,
  // Format and shape changes to existing media: convert, trim, crop,
  // compress. The trailing target (a format, a timestamp, a size) is what
  // keeps "cut the video budget to $500" out.
  /\b(convert|turn|make|export|trim|cut|crop|clip|shorten|split|loop|reverse|speed up|slow down|compress|resize|downscale|mute|strip|extract|pull)\b.{0,40}\b(video|clip|audio|mp3|mp4|gif|movie|footage|recording|song|track|podcast|episode|images?|photos?|pictures?|screenshots?|voice memo|voicemail)\b.{0,50}\b(to|into|as|from|between|at|down to|under)\b.{0,30}\b(mp3|mp4|wav|m4a|aac|flac|ogg|webm|mov|mkv|avi|gif|png|jpe?g|webp|svg|pdf|\d+:\d+|\d+\s?(?:s|sec|seconds?|min|minutes?|kb|mb|px|p|fps)\b|audio|video|text|square|vertical|portrait|landscape|9:16|16:9|1:1)/i,
  // "I need this video as an mp4" — the format named is the deliverable.
  /\b(need|want|get|give me|send me)\b.{0,30}\b(this|that|the)\s+(?:[\w'-]+\s+){0,2}(video|clip|audio|song|track|image|photo|picture|file|recording|episode)\b.{0,20}\b(as|in)\s+(?:a |an )?(?:\w+\s+)?(mp4|mp3|wav|m4a|gif|png|jpe?g|webp|pdf|docx?|file)\b/i,
  // A web page turned into a file the user keeps: PDF, markdown, an
  // offline copy. Summarizing the same page is chat.
  /\b(save|archive|snapshot|print|export|convert|turn|render|download)\b.{0,30}\b(this|that|the|these|each|every|all the)\s+(?:[\w'-]+\s+){0,2}(page|pages|site|website|webpage|web page|article|post|tweet|thread|url|link|links|doc|google doc|notion page|wiki|newsletter|issue|recipe|listing)\b.{0,40}\b(to|as|into|in)\b.{0,20}\b(a |an )?(pdf|markdown|md|docx?|word|epub|html|png|jpe?g|screenshot|image|file|text file|plain text|offline copy)\b/i,
  // Capturing a page or post takes a browser.
  /\b(screenshot|screen[- ]?shot|screen capture|screengrab)\b.{0,30}\b(this|that|the|these|every|each)\s+(?:[\w'-]+\s+){0,2}(page|site|website|tweet|post|thread|url|link|article|profile|listing|dashboard|homepage)\b/i,
  /\b(unroll|unthread)\b.{0,20}\b(this|that|the)\s+thread\b/i,
  // Collecting every attachment or reply under one post or account.
  /\b(save|export|download|compile|collect|grab|get|pull|scrape)\b.{0,30}\b(all|every|each)\s+(?:the\s+|of the\s+)?(replies|comments|quote tweets|quotes|posts|images|photos|videos|clips|attachments|links|pdfs)\b.{0,40}\b(from|in|on|under)\s+(this|that|the)\s+(thread|post|tweet|page|profile|account|channel|playlist|album|subreddit|video|article)\b/i,
];

const BATCH_LONG_RUNNING = [
  /\b(work through|go through|process)\b.{0,20}\b(every|each|all) (item|one|of (these|them|the))\b/i,
  /\b(for each of (these|them)|each of (these|them),)\b.{0,40}\b(draft|write|create|build|respond|reply)\b/i,
  // Same ask with the verb in front: "draft replies to each of these emails".
  /\b(draft|write|create|build|respond|reply|send|generate|make|summari[sz]e|review|rewrite)\b.{0,50}\b(for |to |on |against )?each of (these|those|the|my)\b/i,
  /\b(keep going until|don't stop until|do not stop until)\b/i,
  /\bbatch[- ]process\b/i,
  /\bprocess all of (these|the|them)\b/i,
  /\b(enrich|dedupe|de[- ]duplicate|normalize|standardize)\b.{0,40}\b(this|these|the)\s+(list|rows|records|entries|contacts|leads|addresses|data|dataset)\b/i,
  // The adjective slot matters: people say "my shopify products", not
  // "my products".
  /\b(go through|process|review)\b.{0,30}\b(all|these|the|my|our)\s+(\w+\s+){0,2}(\d+|rows|records|entries|emails|tickets|files|pages|listings|products|orders|customers|posts|photos)\b/i,
  /\bone (per|for each)\b/i,
];

const POLISH_EXPAND = [
  /\b(polish|rewrite|tighten)\b.{0,30}\b(this|the)\s+(draft|notes|memo|essay)\b.{0,40}\b(into|as)\b.{0,30}\b(professional|document|memo|file)\b/i,
  /\bexpand (these|the|my)\s+(bullet points|bullets|outline|notes)\b.{0,40}\binto\b.{0,30}\b(full |a )?(proposal|document|paper|brief|article|post)\b/i,
  /\bturn (my|these|the)\s+notes\b.{0,40}\binto\b.{0,40}\b(blog|series|document|paper|proposal|article)\b/i,
  /\bbreak this (project|work|goal)\b.{0,40}\binto tasks\b/i,
];

const DATA_ANALYSIS = [
  /\banaly[sz]e this (dataset|data|csv|spreadsheet|workbook)\b/i,
  /\b(chart|plot|visualize)\b.{0,30}\b(trends?|this data)\b.{0,40}\b(findings|write|report)\b/i,
  /\breconcile (these|the) (two |2 )?(exports?|spreadsheets?|files|books)\b/i,
  /\b(create|build|make)\b.{0,30}\b(financial model|operating model|cap table)\b/i,
];

const DECISION_COMPARE = [
  /\b(compare|comparison)\b.{0,70}\b(vendors?|options|tools|products|providers|candidates|plans|alternatives)\b.{0,50}\b(recommend|score|spreadsheet|table|matrix|decision)\w*\b/i,
  /\brecommend\w*\b.{0,50}\b(comparison|spreadsheet|scorecard|decision matrix)\b/i,
  // "Compare these and just tell me which one" — the deliverable is the
  // decision, and the nouns are whatever the user is actually shopping for,
  // so neither the artifact list nor the vendor list above catches it.
  /\b(compare|comparison|evaluate|weigh up|weigh)\b.{0,90}\b(recommend|rank|score|short ?list|tell me which|help me (choose|decide|pick)|which (one )?(to|should i) (buy|pick|choose|use|get|go with)|pick one|best (option|one|choice)|top (\d+|three|five|ten))\b/i,
];

const AGENT_EVALS = [
  /\b(model tournament|llm tournament|bake[- ]?off)\b/i,
  /\b(compare|score|evaluate|rank)\b.{0,50}\b(models?|gemini|claude|arya|gpt)\b.{0,80}\b(export|markdown|comparison|winner|scorecard|tournament)\b/i,
  /\bvirtual company\b.{0,50}\b(org chart|p&l|okrs?|export|spreadsheet)\b/i,
  /\bgrowth audit\b/i,
  /\b(reproduce|reproduction)\b.{0,40}\b(the )?(paper|experiment|methods)\b/i,
];

const TRANSCRIPT_TO_ARTIFACT = [
  /\b(transcript|meeting notes|call notes)\b.{0,50}\b(spec|action items|summary|document|follow-?ups)\b/i,
  /\b(turn|take|convert|repurpose)\b.{0,30}\b(this|the|my)\s+((youtube|zoom)\s+)?(transcript|meeting notes|recording|video|podcast|episode|webinar|interview|livestream|talk)\b/i,
  // Repurposing a recording into something written. The source is media but
  // the deliverable is a document, so this is not a media request at all.
  /\b(video|youtube|podcast|episode|webinar|interview|recording|livestream)\b.{0,70}\b(into|to|as)\b.{0,40}\b(blog post|article|show notes|newsletter|summary|document|thread|write[- ]?up|transcript|spec|action items)\b/i,
];

// --- Domain work products --------------------------------------------------
// Same test as above: would an agent with tools, files, and several passes
// beat one chat reply? Each pattern needs a task verb or a real artifact so
// "what is an NDA?" and "explain terraform" stay in chat.

const LEGAL_WORK = [
  /\b(draft|prepare|write|generate)\b.{0,40}\b(operating agreement|articles of incorporation|nda|non[- ]disclosure|msa|sow|statement of work|employment agreement|contractor agreement|lease|demand letter|cease and desist|privacy policy|terms of service|will|trust)\b/i,
  /\b(case timeline|prior art (search|review)|deposition|discovery documents|exhibits)\b/i,
  /\b(compliance|regulatory|licensing)\b.{0,30}\b(checklist|audit|gap analysis|matrix|review|requirements)\b/i,
  /\b(gdpr|hipaa|soc ?2|ccpa|pci[- ]dss)\b.{0,50}\b(checklist|audit|readiness|polic(y|ies)|documentation|compliance)\b/i,
  /\b(compare|summarize)\b.{0,40}\b(laws|regulations|statutes|rules)\b.{0,50}\b(states|countries|jurisdictions|across)\b/i,
];

const FINANCE_WORK = [
  /\b(build|create|make|prepare|model)\b.{0,40}\b(budget|forecast|financial model|3[- ]statement|three[- ]statement|p&l|pro forma|valuation|cap table|amortization schedule|cash flow (model|projection))\b/i,
  /\b(categorize|classify|reconcile|audit|tally)\b.{0,40}\b(transactions?|expenses?|bank statements?|receipts?|invoices?|ledger|the books)\b/i,
  /\b(compare|evaluate)\b.{0,40}\b(loans?|mortgages?|insurance (policies|quotes|plans)|retirement (plans|options)|vendors?|quotes|bids)\b.{0,60}\b(spreadsheet|table|recommend|scenario|option|side by side)\w*\b/i,
  /\b(tax|bookkeeping|payroll)\b.{0,40}\b(organize|prepare|categorize|reconcile|summar|checklist)\w*\b/i,
];

const HIRING_WORK = [
  /\b(screen|rank|score|shortlist|evaluate|sort)\b.{0,40}\b(resumes?|cvs?|candidates?|applicants?|applications)\b/i,
  /\b(write|draft|create)\b.{0,30}\b(job descriptions|jds)\b/i,
  /\b(interview (kit|guide|scorecard|loop|plan)|hiring plan|onboarding (plan|checklist|guide|doc)|employee handbook|comp(ensation)? (bands?|benchmark(ing)?)|performance review template)\b/i,
  /\b(tailor|customize|rewrite|adapt|update)\b.{0,30}\b(my )?(resume|cv|cover letter)\b/i,
];

const MARKETING_WORK = [
  /\b(build|create|compile|source)\b.{0,40}\b(lead list|prospect list|media list|influencer list|target account list|icp)\b/i,
  /\b(keyword research|seo audit|content plan|editorial calendar|content calendar|backlink (audit|analysis)|competitor teardown)\b/i,
  /\b(write|draft|create|generate)\b.{0,40}\b([3-9]|\d{2,})\s+(blog posts?|articles?|emails?|ads?|ad variants?|variants?|landing pages?|social posts?|captions?|scripts?)\b/i,
  /\b(launch|campaign|go[- ]to[- ]market|gtm|marketing)\b.{0,30}\b(plan|brief|strategy|checklist|calendar|playbook)\b/i,
  /\b(pitch deck|investor deck|sales deck|board deck|one[- ]pager|press release|case study|white ?paper)\b/i,
];

const PRODUCT_DOCS_WORK = [
  /\b(write|draft|create|produce|put together)\b.{0,40}\b(prd|product requirements|roadmap|user stories|release notes|changelog|sop|standard operating procedure|runbook|playbook|style guide|handbook|onboarding guide|api docs|technical spec|design doc|rfc)\b/i,
  /\b(analyze|synthesize|go through|cluster|summarize)\b.{0,40}\b(user (feedback|interviews|research)|support tickets|survey (responses|results|data)|customer reviews|nps (data|responses|scores))\b/i,
  /\b(categorize|tag|triage|label)\b.{0,40}\b(tickets?|feedback|responses?|reviews?|bugs?|issues?|leads?)\b/i,
  // Standing up a reference corpus out of material that already exists.
  /\b(build|create|write|assemble|put together|set up|start)\b.{0,44}\b(a |an |our |my )?(internal )?(wiki|knowledge base|kb|faq|help ?cent(er|re)|documentation site|internal docs|handbook)\b/i,
  /\bturn\b.{0,50}\binto\b.{0,30}\b(a |an )?(wiki|knowledge base|faq|help ?cent(er|re)|documentation|docs|handbook|glossary)\b/i,
];

const EDUCATION_WORK = [
  /\b(build|create|write|draft|design|prepare|make)\b.{0,40}\b(curriculum|syllabus|course outline|study guide|practice (exam|test)|answer key|grading rubric|worksheets|flashcards|lesson plans)\b/i,
  /\blesson plans?\b.{0,40}\b(for (a|the|this|each|every|\d+)\s*(unit|week|month|semester|chapter|lesson|days?)|series)\b/i,
  /\b(annotated bibliography|thesis|dissertation|term paper|capstone)\b/i,
  /\b(grade|assess)\b.{0,20}\b(these|all|the)\s+(essays?|papers?|assignments?|submissions?|tests?)\b/i,
];

const LOCALIZATION_WORK = [
  /\b(translate|localize)\b.{0,50}\b(into|to)\b.{0,30}\b([2-9]|\d{2,}|multiple|several|two|three|four|five)\s+languages\b/i,
  /\b(translate|localize)\b.{0,40}\b(the )?(whole|entire|all)\b.{0,30}\b(site|website|app|book|manual|docs|documentation|pages|strings|files)\b/i,
  /\bi18n\b|\binternationaliz(e|ation)\b/i,
];

const ENGINEERING_OPS = [
  /\b(security|accessibility|a11y|performance|seo|dependency|licen[cs]e|code)\s+audit\b/i,
  /\b(upgrade|bump)\b.{0,30}\b(dependencies|packages|to (react|node|python|django|rails|next))\b/i,
  /\b(analyze|parse|dig through|go through)\b.{0,30}\b(logs?|stack traces?|crash reports?|traces|telemetry)\b/i,
  /\b(write|optimize|design)\b.{0,30}\b(sql (queries|schema)|database schema|migrations?|indexes)\b/i,
  /\b(write|create|set up|configure|deploy|provision|fix|debug)\b.{0,40}\b(terraform|kubernetes|k8s|helm|ansible|cloudformation|ci pipeline|dockerfile)\b/i,
  /\b(build|create)\b.{0,30}\b(dashboard|etl|data pipeline|scraper|crawler|test suite)\b/i,
  /\b(triage|fix)\b.{0,30}\b(flaky tests?|lint (errors|warnings)|type errors|ci failures?)\b/i,
];

const LONGFORM_CREATIVE = [
  // "book cover" is one image and stays in chat. "booking" is already
  // excluded by the word boundary on book.
  /\b(write|outline|draft|ghostwrite|make|create|illustrate)\b.{0,40}\b(a |an |my |me a )?(cute |short |little |small )?(children'?s |kids? |picture |story |board )?(book|ebook|novel|novella|memoir|screenplay|stage play|comic script)(?!\s*(cover|jacket|spine|mark|shelf|store|club))\b/i,
  /\b(create|build|design)\b.{0,30}\b(a |an )?(course|curriculum|training program|workshop|bootcamp)\b/i,
  /\b(newsletter series|podcast (season|series)|video series|blog series|chapter[- ]by[- ]chapter|multi[- ]part (series|guide))\b/i,
  // A campaign is a stack of linked artifacts — sessions, maps, characters
  // — not a single creative reply.
  /\b(build|create|write|design|run|prep)\b.{0,40}\b(a |an |me a )?(\w+[- ]?session |multi[- ]session |\d+[- ]?(session|part|episode|chapter) )?(campaign|adventure|module|one[- ]shot)\b.{0,50}\b(with|including|maps?|npcs?|characters?|encounters?|sessions?)\b/i,
];

const PLANNING_LOGISTICS = [
  /\b(plan|organize|arrange)\b.{0,40}\b(trip|vacation|itinerary|travel|wedding|event|conference|offsite|retreat|move|renovation|remodel|party)\b/i,
  /\b(itinerary|packing list|seating chart|guest list)\b.{0,40}\b(build|create|make|plan|spreadsheet|day by day|with)\b/i,
  /\b(meal plan|workout (program|plan)|training (plan|program)|study plan|reading list)\b.{0,50}\b(week|month|weeks|months|\d+\s*days?|spreadsheet|grocery list|tracker|schedule)\b/i,
  /\b(rfp|rfi|grant (application|proposal)|bid|tender|permit application)\b/i,
];

// Bureaucracy. Every one of these is research plus a document plus a
// deadline, and it is the category people most expect to lose an afternoon
// to. Asking *how* an appeal works is still chat — HOW_TO_EXPLAIN and
// ADVICE_QA hold that line, so these only need to catch the delegating form.
const PERSONAL_ADMIN = [
  /\b(dispute|appeal|contest|challenge|fight|protest|negotiate)\b.{0,44}\b(bill|billing|charge|invoice|claim|denial|decision|ticket|fine|penalty|assessment|valuation|rejection|late fee|chargeback|rent increase)\b/i,
  /\b(fill (out|in)|complete|prepare|assemble|put together|walk me through filing|submit|file)\b.{0,44}\b(application|paperwork|forms?|petition|claim|complaint|appeal|return|filing|registration|renewal|dispute)\b/i,
  /\b(visa|green card|passport|citizenship|naturalization|fafsa|financial aid|medicaid|medicare|social security|unemployment|disability|workers.? comp|permit|licen[cs]e|grant|scholarship|tax return|insurance claim|small claims|probate|custody)\b.{0,70}\b(appl(y|ication)|file|filing|prepare|paperwork|documents|checklist|eligib|qualify|deadline|what (do )?i need|everything i need)\w*\b/i,
  /\b(what|which)\s+(documents|paperwork|forms|records)\b.{0,44}\b(do i need|are required|to (apply|file|submit|bring))\b/i,
  // Same ask with the verb in front of the programme name.
  /\b(prepare|gather|assemble|gather up|apply for|renew|everything i need for|what i need for|get ready for)\b.{0,44}\b(visa|green card|passport|citizenship|fafsa|financial aid|medicaid|medicare|social security|unemployment|disability|permit|licen[cs]e|grant|scholarship|tax(es| return)|insurance claim|open enrollment)\b/i,
  /\b(insurance|coverage|health plan|medicare|medicaid|benefits)\b.{0,70}\b(compare|which (one|plan)|best (one|plan|option)|cheapest|recommend|for (my|our) (family|kids|situation|business|parents))\b/i,
  /\b(cancel|audit|go through|review)\b.{0,40}\b(my|our|these|all)\b.{0,24}\b(subscriptions?|recurring charges|memberships?|bills?|policies)\b/i,
];

// Buying something big enough to research. The constraint — a budget, a
// place, a must-have list — is what separates a shopping job from a question
// like "which laptop is best", which chat answers fine.
const SHOPPING_RESEARCH = [
  /\b(find|look for|search for|shop for|source|help me (find|buy|pick|choose|shop))\b.{0,70}\b(under|below|less than|within|up to|around|budget of|no more than)\s*\$?\s?\d/i,
  // A real amount of money next to a buying verb. Both halves are required:
  // a bare number is "I have 2 cats", and a bare verb is "should I buy a
  // house" — a question ADVICE_QA already sends to chat.
  /\b(buy|purchase|shop for|shopping for|looking for|in the market for|upgrade to|replace my)\b.{0,70}(\$\s?\d[\d,.]*|\b\d[\d,.]*\s*(k\b|grand\b|dollars\b|usd\b))/i,
  /\b(my budget is|budget of|looking to spend|willing to spend|can spend|have)\s*(about|around|roughly|up to)?\s*(\$\s?\d[\d,.]*|\b\d[\d,.]*\s*(k\b|grand\b|dollars\b|usd\b))\b.{0,70}\b(for|on|to (buy|get|replace|upgrade))\b/i,
  /\b(find|look for|search for|help me (find|buy|pick|choose)|shop for|compare)\b.{0,70}\b(house|home|apartment|condo|townhouse|rental|car|truck|suv|minivan|laptop|phone|camera|mattress|stroller|bike|e[- ]?bike|grill|fridge|dishwasher)s?\b.{0,70}\b(compare|options|short ?list|near|in my area|nearby|with|that|under|best|reliable|reviews?)\b/i,
  /\b(houses?|homes?|apartments?|condos?|properties|listings?)\b.{0,60}\b(bedrooms?|bath|sq ?ft|square feet|school district|good schools|neighborhood|walkable|commute)\b/i,
];

const VERIFY_QA_WORK = [
  /\bfact[- ]check\b/i,
  /\bverify (each|every|all|the) (claim|fact|citation|source|number|figure)s?\b/i,
  /\b(find|fix|remove|clean up)\b.{0,30}\b(every|all)\b.{0,30}\b(broken links?|dead links?|typos?|errors?|inconsistencies|duplicates|dead code|unused (code|imports|files|dependencies|css)|todos?|vulnerabilities|security (issues|holes)|missing (alt text|tests|translations))\b/i,
  /\baudit\b.{0,30}\b(docs|documentation|site|website|content|copy|spreadsheet|data)\b/i,
  /\b(double[- ]check|check) your work\b/i,
  /\b(make sure (it|everything) (works|runs|passes)|run the tests? (and|then) fix|until (it|they|the tests?) pass)/i,
];

// Signals that the user is delegating, not asking — these travel across every
// domain, so they matter as much as the nouns above.
const AUTONOMY_SIGNALS = [
  /\b(handle|take care of|own|finish)\b.{0,24}\b(this|it|the rest|everything)\b.{0,30}\b(end to end|for me|from here|yourself|on your own)\b/i,
  /\b(build|create|write|implement|make|set up|design|do)\b.{0,40}\b(from scratch|end[- ]to[- ]end|start to finish)\b/i,
  /\b(don'?t ask me|without asking me|use your (own )?judg?ment|you decide|your call)\b/i,
  /\b(iterate|keep (going|working|refining|improving)|refine|revise)\b.{0,30}\b(until|till)\b/i,
  /\b(be (thorough|exhaustive|comprehensive)|take your time|as (thorough|complete|detailed) as possible|leave nothing out|don'?t (cut corners|skip))\b/i,
  // Handing over the wheel without naming a deliverable. The user is not
  // going to specify further — that is the point — so nothing else in the
  // catalog will ever match these.
  // "do your best" is politeness, not delegation, so it is deliberately not
  // in this list.
  /\b(run with (this|it)|see what you can (do|come up with|find)|show me what you (come up with|can do)|take (a|your) (crack|shot|pass|stab) at|do your (thing|worst)|go nuts|have at it)\b/i,
  /\b(figure (it|this) out|sort (it|this) out|work (it|this) out)\b.{0,24}\b(for me|yourself|on your own|end to end)\b/i,
];

const DELIVERABLE_READY = [
  /\b(client|board|investor|press|print|publication|customer|court|audit)[- ]ready\b/i,
  /\bready to (send|ship|publish|present|submit|hand off|file)\b/i,
  /\b(final (deliverable|version|draft|copy|package)|finished (draft|document|deck|product))\b/i,
  /\bso I can (share|send|hand|present|submit) (it|this|these)\b/i,
];

const MULTI_ARTIFACT_SCALE = [
  /\b([2-9]|\d{2,})\s*[- ]?(page|slide)s?\s+(report|paper|document|guide|brief|deck|presentation|ebook|white ?paper|memo|book|storybook|picture book)\b/i,
  /\b([1-9]\d{3,})\s*[- ]?words?\b/i,
  /\b(all (\d+|fifty|50) states|every (state|country|jurisdiction|region))\b/i,
  /\b(the|my|our) (whole|entire) (site|website|repo|repository|codebase|book|dataset|document|manual|catalog|library|inventory|backlog)\b/i,
  /\b(and also (make|create|write|build)|plus (a|an) (deck|spreadsheet|report|document|summary)|along with (a|an) )/i,
];

// Media asks that are production jobs rather than a single generation. The
// Work agent makes images, video, and music too, and it is strictly better at
// these than chat: it can produce a dozen assets in one run, carry a style
// across them, and revise without the user re-prompting per asset.
// isPureMediaRequest still keeps a single "make an image of a castle" off the
// suggestion chip; classified media intents auto-upgrade on the backend.
const MEDIA_PRODUCTION = [
  // An explicit count of assets. Three is the floor: "two logos" is a chat
  // turn, a dozen thumbnails is an afternoon. Style words between the count
  // and the noun ("12 vintage youtube thumbnails") used to miss.
  /\b([3-9]|\d{2,}|three|four|five|six|seven|eight|nine|ten|twelve|dozen|fifteen|twenty|thirty|fifty)\s+(?![\s-]*(?:second|sec|minute|min|hour)s?\b)(?:different\s+|unique\s+|separate\s+|distinct\s+|custom\s+)?(?:[\w']+[\s-]+){0,6}(images?|pictures?|photos?|illustrations?|logos?|posters?|banners?|thumbnails?|icons?|avatars?|videos?|clips?|animations?|scenes?|shots?|stills?|variations?|variants?|mockups?|renders?|covers?|posts?)\b/i,
  /\b(a\s+)?(set|series|batch|collection|pack|suite|sheet)\s+of\s+(?:[\w']+[\s-]+){0,4}(images?|pictures?|photos?|illustrations?|logos?|posters?|banners?|thumbnails?|icons?|avatars?|videos?|clips?|animations?|scenes?|shots?|stills?|mockups?|renders?)\b/i,
  // One asset per item in a list — the shape of a real batch run.
  /\bone\s+(image|picture|photo|illustration|video|clip|render|mockup|icon)\b.{0,20}\b(per|for each|each)\b/i,
  /\b(for each|per)\s+(scene|chapter|product|sku|slide|section|episode|page|character|listing|item|room)\b.{0,60}\b(image|picture|photo|illustration|video|clip|render|art|mockup)\b/i,
  // Named multi-asset deliverables. Each of these is understood to be a
  // package, so the plural is implied by the noun itself.
  /\b(brand identity|brand kit|branding (package|kit)|style guide|storyboard|ad campaign|advertising campaign|marketing assets|social (media )?kit|asset pack|icon set|sprite ?sheet|mood ?board|character sheet|book illustrations|album art(work)?|product photos|photo ?shoot|look ?book)\b/i,
  // A counted picture book is N illustrations plus layout, not one image.
  // Three is the floor so "a 2 page flyer" stays a single generation.
  /\b([3-9]|\d{2,}|three|four|five|six|seven|eight|nine|ten|twelve|fifteen|twenty)\s*[- ]page\s+(picture\s+|story\s+|children'?s\s+|kids?\s+|board\s+|illustrated\s+)?(book|storybook|story|comic)\b/i,
  /\b(make|create|write|illustrate|design)\b.{0,50}\b(a |an |me a )?(cute |little |short )?(picture book|storybook|children'?s book|kids? book|board book|comic book|graphic novel)\b/i,
  /\b(make|create|write|illustrate|design)\b.{0,50}\b(a |an |me a )?(comic|graphic novel)\s+about\b/i,
  // Length is its own kind of plurality. Video models emit a few seconds
  // per generation, so anything measured in minutes — or past a short clip
  // in seconds — is necessarily several shots plus an audio bed and an
  // assembly pass, however singular the noun sounds. Under ~15s is still a
  // single generation for the suggestion chip; a classified create-video
  // intent auto-upgrades regardless.
  // Style words sit between the duration and the noun in ordinary speech
  // ("one minute 90's sitcom style show video") — they used to break the
  // match and send a minute-scale ask to chat.
  /\b(make|create|generate|produce|shoot|edit|cut|put together|build|do|need|want|give|get|film|animate|render)\b.{0,44}\b(\d+|a|one|two|three|four|five|ten|fifteen|thirty|sixty|ninety)[\s-]*(minute|min|hour)s?[\s-]*(long\s+)?(?:[\w']+[\s-]+){0,8}(video|clip|film|ad|advert|advertisement|commercial|animation|reel|short|documentary|trailer|promo|montage|explainer|spot|tiktok|sitcom|episode|demo|scene)\b/i,
  /\b(make|create|generate|produce|shoot|edit|cut|put together|build|do|need|want|give|get|film|animate|render)\b.{0,44}\b(1[5-9]|[2-9]\d|\d{3,}|fifteen|twenty|thirty|forty|fifty|sixty|ninety)[\s-]*(second|sec)s?[\s-]*(long\s+)?(?:[\w']+[\s-]+){0,8}(video|clip|film|ad|advert|advertisement|commercial|animation|reel|short|documentary|trailer|promo|montage|explainer|spot|tiktok|sitcom|episode|demo|scene)\b/i,
  // Same job with the length after the noun: "a video that is one minute long".
  /\b(make|create|generate|produce|shoot|edit|cut|put together|build|do|need|want|give|get|film|animate|render)\b.{0,80}\b(video|clip|film|ad|advert|advertisement|commercial|animation|reel|short|documentary|trailer|promo|montage|explainer|spot|tiktok|sitcom|episode|demo|scene)\b.{0,48}\b(\d+|a|one|two|three|four|five|ten|fifteen|thirty|sixty|ninety)[\s-]*(minute|min|hour)s?\b/i,
  /\b(make|create|generate|produce|shoot|edit|cut|put together|build|do|need|want|give|get|film|animate|render)\b.{0,80}\b(video|clip|film|ad|advert|advertisement|commercial|animation|reel|short|documentary|trailer|promo|montage|explainer|spot|tiktok|sitcom|episode|demo|scene)\b.{0,48}\b(1[5-9]|[2-9]\d|\d{3,}|fifteen|twenty|thirty|forty|fifty|sixty|ninety)[\s-]*(second|sec)s?\b/i,
  // Formats that are composite by definition: each needs a script, a voice,
  // music, and more than one shot before it is finished. sitcom is a show,
  // not a sitcom-style poster — the lookahead keeps the modifier out.
  /\b(make|create|generate|produce|shoot|put together|build|film|edit|cut)\b.{0,44}\b(commercial|infomercial|explainer|music video|short film|documentary|trailer|promo video|sizzle reel|demo video|product demo|tutorial video|how-to video|animated short|comedy sketch|highlight reel|podcast episode|stop[- ]motion|teaser(?:\s+for)|(?:tv |cooking |game |talk |news |cartoon )?show episode|sitcom(?!\s+style))\b/i,
  // Several shots, or assembling clips, is an edit even without a duration.
  /\b(multi[- ]scene|multiple scenes|several scenes|a few scenes)\b.{0,40}\b(video|clip|film|ad|commercial|animation|sitcom)\b/i,
  /\b(video|clip|film|ad|commercial|animation|sitcom)\b.{0,40}\b(multi[- ]scene|multiple scenes|several scenes)\b/i,
  /\b(stitch|assemble|cut together|edit together|compile)\b.{0,40}\b(clips?|footage|shots?|videos?)\b/i,
  // An album is a batch of tracks, not one song.
  /\b(make|create|produce|record)\b.{0,40}\b(an?\s+)?(album|ep|mixtape)\b/i,
  // Music: lyrics with section markers plus a production brief, or an
  // explicit generate/compose/produce of a song or track. Chat still gets
  // "what's a good song for a wedding" via ADVICE_QA and "what is a ballad"
  // via DEFINITIONAL_QA. A one-line lyric ask without a track brief stays
  // write-lyrics-in-chat.
  /\[[^\]]*(verse|chorus|bridge|outro|intro|hook|pre-?chorus|refrain|coda)[^\]]*\][\s\S]{0,1200}\b(arrangement|the track is|fingerpicked|instrumentation|harmonica|brushed percussion|weathered (male |female )?vocal|produced|stems|mix(?:ed|ing)?|master(?:ed|ing)?)\b/i,
  /\bthe track is\b.{0,200}\b(arrangement|acoustic|electric|orchestral|fingerpicked|harmonica|guitar|piano|vocal|drums|percussion|fiddle|banjo|strings|synth)\b/i,
  /\b(generate|create|make|compose|produce|record|render)\b.{0,50}\b(a |an |me a |the )?(folk |country |bluegrass |appalachian |gospel |jazz |blues |rap |pop |rock |acoustic |lo-?fi )?(song|track|ballad|lullaby|anthem|hymn|jingle|instrumental)(?!\s*(recommendation|meaning|title|name))\b/i,
  /\b(folk|country|bluegrass|appalachian|gospel)\s+song\s+about\b/i,
  // A media pipeline: generate, then transform what was generated.
  /\b(image|images|picture|photo|illustration|art)\b.{0,40}\b(then|and then|after that)\b.{0,40}\b(animate|turn it into a video|make it move|add (music|a soundtrack|audio|voice ?over|narration))\b/i,
  /\b(animate|add (music|a soundtrack|voice ?over|narration))\b.{0,40}\b(each|them|all of them|every (one|image|scene|clip))\b/i,
  // Face composite / multi-reference merge. One output, but it needs every
  // source image and usually a second model if the first refuses likeness.
  /\b(put|place|swap|paste|map)\b.{0,40}\b(my |the |his |her |their )?(face|head|likeness)\b/i,
  /\b(face[- ]?swap|faceswap)\b/i,
  /\b(combine|merge|composite|compose|fuse|blend)\b.{0,50}\b(these|the|two|2|both|my)\s+(images?|pictures?|photos?|pics|references?)\b/i,
  /\b(combine|merge|composite|compose|fuse)\b.{0,40}\b(images?|pictures?|photos?)\b/i,
];

// Distinct kinds of asset, for spotting an ask that spans more than one.
// Grouped by interchangeable synonym so "a poster and a flyer" reads as one
// kind — the pairs that matter are across groups, like a logo plus a video.
const MEDIA_ARTIFACT_KINDS = [
  /\b(images?|pictures?|photos?|illustrations?|artwork|drawings?)\b/i,
  /\b(logos?|icons?|favicons?|wordmarks?)\b/i,
  /\b(posters?|banners?|flyers?|thumbnails?|covers?)\b/i,
  /\b(videos?|clips?|animations?|reels?|trailers?)\b/i,
  /\b(songs?|music|tracks?|jingles?|soundtracks?)\b/i,
  /\b(voice ?overs?|narration|audio)\b/i,
  /\b(3d models?|meshes|renders?|sculpts?)\b/i,
];

// Wording that separates two assets from one asset described twice. Without
// it "design a logo image" counts as two kinds and would wrongly escalate.
const MULTI_ASSET_CONNECTOR = /(,|\band\b|\bthen\b|\bplus\b|\balso\b|\bas well as\b|\balong with\b)/i;

function isMediaProductionJob(text) {
  const normalized = normalizeWorkModeText(text);
  if (!normalized) return false;
  if (any(normalized, MEDIA_PRODUCTION)) return true;
  let kinds = 0;
  for (let i = 0; i < MEDIA_ARTIFACT_KINDS.length; i += 1) {
    if (MEDIA_ARTIFACT_KINDS[i].test(normalized)) kinds += 1;
  }
  return kinds >= 2 && MULTI_ASSET_CONNECTOR.test(normalized);
}

const ALL_SCENARIO_GROUPS = [
  MEDIA_PRODUCTION,
  PROJECT_SOFTWARE,
  MULTI_STEP,
  RESEARCH_DELIVERABLE,
  FILE_DOCUMENT_WORK,
  ARTIFACT_AGENT,
  FIND_AND_COLLECT,
  MONITOR_RECURRING,
  TOOL_MIGRATION,
  DIFF_RECONCILE,
  MULTI_VARIANT,
  ITERATE_TO_TARGET,
  TEARDOWN_ANALYSIS,
  DIAGNOSE_INVESTIGATE,
  AUTOMATION_SCRIPTING,
  AGENT_FRAMING,
  CATALOG_INVENTORY,
  WEB_AGENT,
  MEDIA_FETCH_CONVERT,
  BATCH_LONG_RUNNING,
  POLISH_EXPAND,
  DATA_ANALYSIS,
  DECISION_COMPARE,
  AGENT_EVALS,
  TRANSCRIPT_TO_ARTIFACT,
  LEGAL_WORK,
  FINANCE_WORK,
  HIRING_WORK,
  MARKETING_WORK,
  PRODUCT_DOCS_WORK,
  EDUCATION_WORK,
  LOCALIZATION_WORK,
  ENGINEERING_OPS,
  LONGFORM_CREATIVE,
  PLANNING_LOGISTICS,
  PERSONAL_ADMIN,
  SHOPPING_RESEARCH,
  PREP_FOR_EVENT,
  VERIFY_QA_WORK,
  AUTONOMY_SIGNALS,
  DELIVERABLE_READY,
  MULTI_ARTIFACT_SCALE,
];

// Groups whose presence means a media ask is more than one generation —
// either it also produces a non-media work product, or MEDIA_PRODUCTION says
// the media itself is a batch. The four generic signals are excluded because
// they ride along on an ordinary media ask without adding any work: "make an
// image of a castle, and be thorough" is still one image.
const BEYOND_SINGLE_ASSET_GROUPS = ALL_SCENARIO_GROUPS.filter((group) => (
  group !== MULTI_STEP
  && group !== AUTONOMY_SIGNALS
  && group !== DELIVERABLE_READY
  && group !== MULTI_ARTIFACT_SCALE
));

function normalizeText(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

function isMediaGenerationQuery(text) {
  const query = normalizeText(text);
  if (!query) return false;
  const tokens = query.toLowerCase().split(/\s+/);
  return isImageRequestQuery(query)
    || isMusicCreationQuery(tokens, query)
    || isVideoCreationQuery(tokens, query)
    || is3DCreationQuery(tokens, query)
    || isVectorRequestQuery(tokens, query);
}

function isWorkHeavyFile(file) {
  if (!file || typeof file !== 'object') return false;
  const name = String(file.name || file.filename || file.originalName || '');
  const mime = String(file.mimeType || file.mimetype || file.type || '');
  if (IMAGE_FILE_EXT.test(name) || /^image\//i.test(mime)) return false;
  if (WORK_HEAVY_FILE_EXT.test(name)) return true;
  if (WORK_HEAVY_MIME.test(mime)) return true;
  return false;
}

function isArchiveFile(file) {
  if (!file || typeof file !== 'object') return false;
  const name = String(file.name || file.filename || file.originalName || '');
  const mime = String(file.mimeType || file.mimetype || file.type || '');
  return ARCHIVE_FILE_EXT.test(name) || /zip|compressed/.test(mime);
}

function getFileIds(files) {
  if (!Array.isArray(files)) return [];
  return files
    .map((file) => {
      if (!file) return null;
      if (typeof file === 'string' || typeof file === 'number') return String(file);
      return file.id || file._id || null;
    })
    .filter(Boolean)
    .map(String);
}

function getFileCount(files) {
  if (!Array.isArray(files)) return 0;
  return files.filter(Boolean).length;
}

function isSimpleQuestion(normalized) {
  if (normalized.length > 140) return false;
  // Mirrors the backend veto: a question opener normally means chat, but
  // "why is my site not ranking, dig into it" opens the same way and is a
  // delegation. DIAGNOSE_INVESTIGATE needs a real subject plus an explicit
  // ask to go find out, so "why is the sky blue" stays vetoed.
  if (
    DEFINITIONAL_QA.test(normalized)
    && normalized.length < 110
    && !any(normalized, DIAGNOSE_INVESTIGATE)
  ) return true;
  if (!SIMPLE_QA.test(normalized)) return false;
  return false;
}

function matchesCompoundTaskList(text) {
  const verbHits = countMatches(text, TASK_VERBS);
  if (verbHits < 3) return false;
  return /[,;]/.test(text) || /\band\b/i.test(text);
}

/**
 * True when the wording belongs in chat no matter what else matches: asking
 * rather than delegating, or a one-shot reply the agent can't improve on.
 * Every Work signal is checked against this first.
 */
function readsLikeChat(text, fileCount = 0) {
  if (!text) return false;
  if (isExplicitChatRequest(text)) return true;
  // "tweet" in SHORT_CREATIVE means writing one. Downloading the video in
  // one, or screenshotting it, is a fetch job and lifts the veto.
  const fetchesMedia = any(text, MEDIA_FETCH_CONVERT);
  if (SHORT_CREATIVE.test(text) && !any(text, MULTI_STEP) && !fetchesMedia) return true;
  if (SINGLE_COVER.test(text) && !isMediaProductionJob(text) && !any(text, MEDIA_PRODUCTION)) return true;
  // "can you make images?" is a capability question. "can you make me a one
  // minute documentary" is a polite commission — only lift for production jobs,
  // not every named work product ("do you have a case study template?").
  if (CAPABILITY_Q.test(text) && text.length < 80 && !isMediaProductionJob(text)) return true;
  if (POSSIBILITY_Q.test(text) && text.length < 90 && !TASK_IMPERATIVE.test(text)) return true;
  // PDF_PREVIOUS is "export the last reply as a PDF". "save this article as
  // a pdf" has the same shape but names a web page, which has to be fetched.
  if (PDF_PREVIOUS.test(text) && fileCount < 2 && !fetchesMedia) return true;
  if (SHORT_TRANSLATE.test(text)) return true;
  if (SHORT_SUMMARY.test(text) && fileCount < 1) return true;
  if (isSimpleQuestion(text) && fileCount < 2) return true;
  if (LEARNING_INTENT.test(text) && fileCount < 2) return true;
  if (
    ADVICE_QA.test(text)
    && fileCount < 2
    && !TASK_IMPERATIVE.test(text)
    && !matchesCompoundTaskList(text)
  ) {
    return true;
  }
  if (
    HOW_TO_EXPLAIN.test(text)
    && !TASK_IMPERATIVE.test(text)
    && !any(text, MULTI_STEP)
    && !any(text, RESEARCH_DELIVERABLE)
    && fileCount < 2
  ) {
    return true;
  }
  // "this query" reads like a snippet until a target is attached to it:
  // "explain this query" is one reply, "optimize this query until it runs
  // under 100ms" is measure, change, measure again.
  if (
    SINGLE_SNIPPET.test(text)
    && fileCount < 2
    && !any(text, PROJECT_SOFTWARE)
    && !any(text, ITERATE_TO_TARGET)
  ) {
    return true;
  }
  return false;
}

/**
 * Whether the suggestion chip should stay off for a media ask. Classified
 * media intents auto-upgrade to Work on the backend; the chip still hides for
 * a one-asset ask so a mid-thread chat that generated media does not also
 * offer Work.
 */
function isPureMediaRequest(text, intent = null) {
  const normalized = normalizeWorkModeText(text);
  const isMedia = !!(intent && MEDIA_INTENTS.has(intent))
    || (!!normalized && (isMediaGenerationQuery(normalized) || MEDIA_REQUEST.test(normalized)));
  if (!isMedia) return false;
  if (isMediaProductionJob(normalized)) return false;
  return !BEYOND_SINGLE_ASSET_GROUPS.some((group) => any(normalized, group));
}

function matchesWorkModeTask(text, { files = [], intent = null, fileCount: fileCountOpt } = {}) {
  if (CHAT_IMAGE_INTENTS.has(intent)) return false;
  const normalized = normalizeWorkModeText(text);
  const fileCount = Number.isFinite(fileCountOpt) ? fileCountOpt : getFileCount(files);
  const heavyFileCount = Array.isArray(files) ? files.filter(isWorkHeavyFile).length : 0;
  const hasArchive = Array.isArray(files) ? files.some(isArchiveFile) : false;

  if (isPureMediaRequest(normalized, intent)) return false;

  if (!normalized && fileCount < 2 && heavyFileCount < 1 && !hasArchive) return false;
  if (normalized && normalized.length < MIN_QUERY_LENGTH && fileCount < 2 && heavyFileCount < 1 && !hasArchive) {
    return false;
  }

  if (normalized && readsLikeChat(normalized, fileCount)) return false;

  if (hasArchive && (TASK_VERBS.test(normalized) || !normalized)) return true;
  if (fileCount >= 2 && (TASK_VERBS.test(normalized) || normalized.length >= MIN_QUERY_LENGTH)) return true;
  if (heavyFileCount >= 1 && TASK_VERBS.test(normalized)) return true;

  if (normalized && ALL_SCENARIO_GROUPS.some((group) => any(normalized, group))) return true;
  // Covers the counted-kinds half of isMediaProductionJob, which is not
  // expressible as a single pattern in MEDIA_PRODUCTION.
  if (normalized && isMediaProductionJob(normalized)) return true;
  if (normalized && matchesCompoundTaskList(normalized)) return true;

  if (normalized && normalized.length >= 480 && countMatches(normalized, TASK_VERBS) >= 2 && !HOW_TO_EXPLAIN.test(normalized)) {
    return true;
  }

  if (intent === 'coding' && normalized && !SINGLE_SNIPPET.test(normalized) && !HOW_TO_EXPLAIN.test(normalized)) {
    if (any(normalized, PROJECT_SOFTWARE) || fileCount >= 1 || /\b(project|codebase|repo|app|tests?|auth|api)\b/i.test(normalized)) {
      return true;
    }
  }
  if (intent === 'research' && (any(normalized, RESEARCH_DELIVERABLE) || fileCount >= 1 || /\b(brief|report|source|recommend)\w*\b/i.test(normalized))) {
    return true;
  }
  if (intent === 'create-longform' && (any(normalized, ARTIFACT_AGENT) || any(normalized, POLISH_EXPAND) || normalized.length > 120)) {
    return true;
  }
  if (
    (intent === 'create-presentation' || intent === 'create-spreadsheet' || intent === 'create-pdf')
    && (fileCount >= 1 || any(normalized, RESEARCH_DELIVERABLE) || any(normalized, FILE_DOCUMENT_WORK) || /\b(compar\w*|research\w*|recommend\w*|from this)\b/i.test(normalized))
  ) {
    return true;
  }

  return false;
}

// A recommendation interrupts a completed answer. Require substantial scope
// AND distinct stages of work; a file, router vote, length, or catalog keyword
// alone is not evidence of complexity. Keep this function mirrored on clients
// so old server stamps cannot reintroduce noisy suggestions.
function hasComplexWorkScope(message) {
  if (isExplicitChatRequest(message)) return false;
  const text = normalizeWorkModeText((typeof message === 'string' ? message : '')
    .replace(/```[\s\S]*?(?:```|$)|`[^`]*`|"[^"]*"|“[^”]*”/g, ' ')
    .replace(/^\s*>.*$/gm, ' ')
    .replace(/((?:https?:\/\/|www\.)[^\s]+?)([,;.!?])(?=\s|$)/gi, '$1 $2')
    .replace(/\b(?:do not|don't|never|without|no need to)\b[^.;!?]*/gi, ' '));
  if (!text || isExplicitChatRequest(text) || HOW_TO_EXPLAIN.test(text)) return false;

  // Count requested actions, not topic words in an essay or pasted material.
  const phase = verbs => new RegExp(`(?:^|[.,;:!?]\\s*|\\b(?:and|then|next|finally|first|please|to)\\s+)(?:${verbs})\\b`, 'i').test(text);
  const gathers = phase('research|investigate|gather|collect|scrape|inspect|crawl');
  const analyzes = phase('analy[sz]e|compare|audit|reconcile|rank|synthesi[sz]e|diagnose');
  const modifies = phase('implement|refactor|migrate|build|fix|debug|integrate');
  const authors = phase('(?:write|draft|create|produce|compile|prepare)(?!\\s+(?:(?:unit|integration|regression|e2e|automated)\\s+)?tests?\\b)');
  const verifies = phase('test|verify|validate|fact[- ]check|(?:run|add|write)\\b.{0,30}\\btests');
  const delivers = phase('deploy|publish|roll out');
  const stages = [gathers, analyzes, modifies, authors, verifies, delivers].filter(Boolean).length;

  const project = /\b(codebase|repository|repo|full[- ]stack|backend|frontend)\b/i.test(text);
  const broadInputs = /\b(?:multiple|several|all|every|three|four|five|six|seven|eight|nine|ten|[3-9]|\d{2,})\s+(?:(?:the|these|attached|source)\s+)*(?:files|documents|sources|papers|studies|sites|websites|datasets|reports|contracts|invoices|vendors|competitors)\b/i.test(text);
  const largeBatch = /\b(?:\d{2,}|hundreds? of|thousands? of)\s+(?:rows|records|invoices|files|products|pages|contacts)\b/i.test(text);
  const deepResearch = /\b(?:comprehensive|in[- ]depth|systematic)\s+(?:research|review|analysis|report)\b|\bliterature review\b/i.test(text);
  const multipleDeliverables = /\b(report|brief|memo)\b/i.test(text)
    && /\b(spreadsheet|slide deck|presentation|dashboard)\b/i.test(text);
  const production = /\b(?:multi[- ]scene|multi[- ]episode|feature[- ]length)\b/i.test(text);

  // A complete app with several subsystems plus tests is inherently staged,
  // even when the prompt states requirements rather than enumerating verbs.
  const subsystems = [
    /\b(auth|authentication|oauth)\b/i, /\b(database|migrations)\b/i,
    /\b(api|backend)\b/i, /\b(frontend|dashboard)\b/i, /\b(payments|billing)\b/i,
  ];
  const buildsApp = /\b(?:build|implement|refactor|migrate)\b.{0,60}\b(?:app|application|codebase|platform)\b/i.test(text);
  if (buildsApp && /\btests?\b/i.test(text) && subsystems.filter(pattern => pattern.test(text)).length >= 2) return true;

  return stages >= 3 && (project || broadInputs || largeBatch || deepResearch || multipleDeliverables || production);
}

function scoreWorkModeIntent(text, options = {}) {
  return shouldSuggestWorkMode(text, options) ? MIN_SCORE : 0;
}

function shouldSuggestWorkMode(text, options = {}) {
  const normalized = normalizeWorkModeText(text);
  const fileCount = Number.isFinite(options.fileCount) ? options.fileCount : getFileCount(options.files);
  return !CHAT_IMAGE_INTENTS.has(options.intent) && !isPureMediaRequest(normalized, options.intent)
    && !readsLikeChat(normalized, fileCount) && hasComplexWorkScope(text);
}

function findUserMessageForAssistant(assistantMessage, messages) {
  if (!assistantMessage || !Array.isArray(messages) || !messages.length) return null;

  if (assistantMessage.parentMessageId) {
    const parent = messages.find(
      (msg) => msg?.role === 'user'
        && (msg.id === assistantMessage.parentMessageId || msg._id === assistantMessage.parentMessageId)
    );
    if (parent) return parent;
  }

  const assistantIndex = messages.findIndex(
    (msg) => msg === assistantMessage
      || (assistantMessage.id && (msg.id === assistantMessage.id || msg._id === assistantMessage.id))
      || (assistantMessage._id && (msg.id === assistantMessage._id || msg._id === assistantMessage._id))
  );

  if (assistantIndex > 0) {
    for (let i = assistantIndex - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'user') return messages[i];
    }
  }

  return null;
}

function getLastAssistantMessage(item) {
  if (!item) return null;
  if (item.type === 'group' && Array.isArray(item.messages) && item.messages.length) {
    for (let i = item.messages.length - 1; i >= 0; i -= 1) {
      if (item.messages[i]?.role === 'assistant') return item.messages[i];
    }
    return null;
  }
  if (item.role === 'assistant') return item;
  return null;
}

function getPendingWorkModeMessage({ messages, isInferencing = false, archivedMessageCount = 0 } = {}) {
  if (isInferencing) return null;
  // The choice endpoint can only resume an unanswered opening prompt. A stale
  // pending flag on an answered turn must use the normal Work handoff instead.
  const pendingUser = Array.isArray(messages) && messages.length === 1 ? messages[0] : null;
  return !archivedMessageCount && pendingUser?.role === 'user'
    && pendingUser.parameters?.___workModeChoice === 'pending' ? pendingUser : null;
}

function resolveWorkModeSuggestion({ lastItem, messages, isInferencing = false, archivedMessageCount = 0 } = {}) {
  if (isInferencing) return null;
  const pendingUser = getPendingWorkModeMessage({ messages, archivedMessageCount });
  if (pendingUser) {
    // Old servers may have paused routine tasks. Hide those suggestions; the
    // chat view uses the pending message to resume the answer automatically.
    const intent = pendingUser.parameters?.___workModeIntent
      || pendingUser.parameters?.___routerIntent || pendingUser.parameters?.___actions?.[0];
    if (!shouldSuggestWorkMode(getMessageText(pendingUser), { files: pendingUser.files, intent })) return null;
    return {
      prompt: normalizeText(getMessageText(pendingUser)),
      fileIds: getFileIds(pendingUser.files || []),
      userMessageId: pendingUser.id || pendingUser._id,
      requiresChoice: true,
    };
  }
  const assistant = getLastAssistantMessage(lastItem);
  if (!assistant || assistant.isStreaming || assistant.isTyping) return null;

  const userMessage = findUserMessageForAssistant(assistant, messages);
  // They already picked normal chat on this turn (opening choice card or
  // dismiss). Do not put a second Work recommendation under the reply.
  if (!userMessage || userMessage.parameters?.___workModeChoice === 'chat') return null;

  const text = getMessageText(userMessage);
  const files = Array.isArray(userMessage.files) ? userMessage.files : [];
  const intent = assistant.parameters?.___workModeIntent
    || userMessage.parameters?.___workModeIntent
    || assistant.parameters?.___routerIntent
    || userMessage.parameters?.___routerIntent
    || assistant.parameters?.___actions?.[0]
    || userMessage.parameters?.___actions?.[0]
    || null;

  // The backend already recognized this as a complete in-chat card. Do not
  // reintroduce the old Work suggestion from broad planning keywords.
  const renderer = assistant.parameters?.___renderer || userMessage.parameters?.___renderer;
  if (!files.length && ['chart', 'recipe', 'workout', 'mealplan', 'checklist', 'itinerary', 'comparison', 'studyplan', 'projectguide', 'eventplan'].includes(renderer)) return null;

  if (CHAT_IMAGE_INTENTS.has(intent)) return null;
  if (isExplicitChatRequest(text)) return null;
  // Recheck every suggestion, including saved positive backend stamps.
  if (!shouldSuggestWorkMode(text, { files, intent })) return null;

  const prompt = normalizeText(text);
  const fileIds = getFileIds(files);
  if (!prompt && !fileIds.length) return null;

  return {
    prompt,
    fileIds,
    userMessageId: userMessage.id || userMessage._id || null,
  };
}

module.exports = {
  MIN_QUERY_LENGTH,
  MIN_SCORE,
  MEDIA_INTENTS,
  shouldSuggestWorkMode,
  scoreWorkModeIntent,
  matchesWorkModeTask,
  isMediaGenerationQuery,
  isPureMediaRequest,
  isMediaProductionJob,
  isWorkHeavyFile,
  getFileIds,
  getPendingWorkModeMessage,
  getMessageText,
  findUserMessageForAssistant,
  resolveWorkModeSuggestion,
};
